import type { AABB, PolygonWithHoles, Vec2 } from '../geo/types';
import type { Channel } from '../data/types';
import { stripPolygon } from '../geo/polyline';
import { pointInPolygon } from '../geo/polygon';
import { fromRing } from '../geo/clip';
import type { ChannelCaps } from './channelJoints';

// A channel end the bounds cut across: the far ring carries the channel on from there along the
// direction of its last stretch.
export interface ChannelExit {
  readonly from: Vec2;
  readonly direction: Vec2;
}

export interface ChannelPath {
  readonly centerline: readonly Vec2[];
  readonly widths: readonly number[];
  readonly exits: readonly ChannelExit[];
}

interface NewEnd {
  readonly points: readonly Vec2[];
  readonly exits: readonly ChannelExit[];
}

const KEPT_END: NewEnd = { points: [], exits: [] };

// Past the bounds line, so that clipping the water to the bounds cuts the whole width of the strip.
const PAST_BOUNDS_M = 1;

function outward(end: Vec2, inner: Vec2): Vec2 | null {
  const dx = end[0] - inner[0];
  const dy = end[1] - inner[1];
  const len = Math.hypot(dx, dy);
  return len > 0 ? [dx / len, dy / len] : null;
}

// How far p travels along the unit direction d before it leaves the box; negative once it has.
function exitDistance(p: Vec2, d: Vec2, box: AABB): number {
  const along = (from: number, dir: number, min: number, max: number) =>
    dir > 0 ? (max - from) / dir : dir < 0 ? (min - from) / dir : Infinity;
  return Math.min(along(p[0], d[0], box.minX, box.maxX), along(p[1], d[1], box.minY, box.maxY));
}

// The GIS data stops the Amstel, the Gaasp and the Zaan 1 m inside the bounds and at an angle to
// them, so their square ends left wedges of land on the bounds and the far ring carried them on at
// half their width. An end that leaves the bounds within one width is carried straight on until
// its whole width is past them.
function pastBounds(end: Vec2, dir: Vec2, width: number, bounds: AABB): Vec2 | null {
  if (exitDistance(end, dir, bounds) > width) return null;
  const half = width / 2;
  const sides: readonly Vec2[] = [
    [end[0] - dir[1] * half, end[1] + dir[0] * half],
    [end[0] + dir[1] * half, end[1] - dir[0] * half],
  ];
  const reach = Math.max(...sides.map((side) => exitDistance(side, dir, bounds))) + PAST_BOUNDS_M;
  return [end[0] + dir[0] * reach, end[1] + dir[1] * reach];
}

function newEnd(
  end: Vec2,
  inner: Vec2,
  width: number,
  inOtherWater: (p: Vec2) => boolean,
  bounds: AABB | null
): NewEnd {
  const dir = outward(end, inner);
  if (!dir) return KEPT_END;
  const intoWater: Vec2 = [end[0] + dir[0] * width, end[1] + dir[1] * width];
  if (inOtherWater(intoWater)) return { points: [intoWater], exits: [] };
  const beyond = bounds && pastBounds(end, dir, width, bounds);
  return beyond ? { points: [beyond], exits: [{ from: end, direction: dir }] } : KEPT_END;
}

// A joined end already meets the other channel along the joint line, so it is not carried on.
function extended(
  channel: Channel,
  inOtherWater: (p: Vec2) => boolean,
  bounds: AABB | null,
  caps: ChannelCaps | undefined
): ChannelPath {
  const pts = channel.centerline;
  const widths = channel.widths;
  const n = pts.length;
  const before = caps?.head ? KEPT_END : newEnd(pts[0], pts[1], widths[0], inOtherWater, bounds);
  const after = caps?.tail
    ? KEPT_END
    : newEnd(pts[n - 1]!, pts[n - 2]!, widths[n - 1]!, inOtherWater, bounds);

  return {
    centerline: [...before.points, ...pts, ...after.points],
    widths: [
      ...before.points.map(() => widths[0]),
      ...widths,
      ...after.points.map(() => widths[n - 1]!),
    ],
    exits: [...before.exits, ...after.exits],
  };
}

// PLAN §5 step 1: an end whose continuation by one width lies in other water is carried there, so
// channels that meet end to end at a slight angle, or run into the IJ, overlap instead of leaving
// a sliver of land between their square ends.
export function extendChannelEnds(
  channels: readonly Channel[],
  openWater: readonly PolygonWithHoles[],
  bounds: AABB | null = null,
  caps: readonly ChannelCaps[] = []
): readonly ChannelPath[] {
  const strips = channels.map((channel) =>
    fromRing(stripPolygon(channel.centerline, channel.widths))
  );
  return channels.map((channel, index) =>
    extended(
      channel,
      (p) =>
        [...openWater, ...strips.filter((_, i) => i !== index)].some((water) =>
          pointInPolygon(p, water)
        ),
      bounds,
      caps[index]
    )
  );
}
